// Singleton WebLLM client. @mlc-ai/web-llm is dynamically imported so its bulk
// (and its WebGPU/WASM machinery) is code-split out of the pattern-only path.
//
// State machine:
//   absent  → WebGPU missing
//   idle    → available, no engine yet
//   loading → model download / WASM compile / weight load in progress
//   ready   → engine usable
//   error   → init failed (recoverable: editor can retry)

import { checkWebGpu } from './feature-detection';
import { extractJson } from './extract-json';
import { createGeminiClient } from './gemini-client';
import { buildLocalAppConfig, type AppConfigLike } from './app-config';
import {
  DEFAULT_MODEL_ID,
  type ChatRequest,
  type ChatResponse,
  type EngineState,
  type LlmClient,
  type LlmProvider,
} from './types';

/** Subset of the @mlc-ai/web-llm API we depend on. */
interface MLCEngineLike {
  chat: {
    completions: {
      create: (req: {
        messages: Array<{ role: 'system' | 'user' | 'assistant'; content: string }>;
        response_format?: { type: 'json_object' } | { type: 'text' };
        max_tokens?: number;
        temperature?: number;
      }) => Promise<{
        choices: Array<{ message: { content: string | null } }>;
        usage?: { total_tokens?: number };
      }>;
    };
  };
  unload?: () => Promise<void>;
}

interface InitProgressReport {
  progress: number;
  text: string;
  timeElapsed?: number;
}

interface EngineOpts {
  initProgressCallback?: (report: InitProgressReport) => void;
  /** Custom model registry. We override model_lib to a same-origin URL. */
  appConfig?: AppConfigLike;
}

interface WebLlmModule {
  prebuiltAppConfig: AppConfigLike;
  CreateMLCEngine: (modelId: string, opts: EngineOpts) => Promise<MLCEngineLike>;
  CreateWebWorkerMLCEngine: (
    worker: Worker,
    modelId: string,
    opts: EngineOpts,
  ) => Promise<MLCEngineLike>;
}

// extractJson (prose/fence-tolerant JSON recovery) is shared with the Gemini
// client — see ./extract-json. WebLLM's grammar `json_object` mode is
// deliberately not used (it hard-crashes some quantised model_lib builds);
// the schema is injected into the system prompt and extractJson + the
// output-validator enforce structure.

function createClient(): LlmClient {
  let state: EngineState = { kind: 'idle', modelId: DEFAULT_MODEL_ID };
  let engine: MLCEngineLike | null = null;
  let inFlight: Promise<void> | null = null;
  const listeners = new Set<(s: EngineState) => void>();

  function setState(next: EngineState): void {
    state = next;
    for (const l of listeners) l(state);
  }

  /**
   * Build a fresh Web Worker + WebLLM engine for `targetModel`. Used both for
   * the initial load and for self-healing recovery. Model *weights* come from
   * the browser Cache API (origin-scoped), so a rebuild after the first load
   * recompiles/uploads to WebGPU without re-downloading the ~5 GB.
   */
  async function createEngine(targetModel: string): Promise<void> {
    const mod = (await import('@mlc-ai/web-llm')) as unknown as WebLlmModule;
    const appConfig = buildLocalAppConfig(mod.prebuiltAppConfig);
    const worker = new Worker(new URL('./worker.ts', import.meta.url), { type: 'module' });
    engine = await mod.CreateWebWorkerMLCEngine(worker, targetModel, {
      appConfig,
      initProgressCallback: (report) => {
        setState({
          kind: 'loading',
          modelId: targetModel,
          progress: report.progress,
          text: report.text,
        });
      },
    });
  }

  function isModelNotLoaded(err: unknown): boolean {
    const m = (err instanceof Error ? err.message : String(err)).toLowerCase();
    return (
      m.includes('not loaded') ||
      m.includes('modelnotloaded') ||
      m.includes('reload(') ||
      m.includes('no model')
    );
  }

  function isDeviceLost(err: unknown): boolean {
    const m = (err instanceof Error ? err.message : String(err)).toLowerCase();
    return (
      m.includes('device was lost') ||
      m.includes('device lost') ||
      m.includes('out of memory') ||
      m.includes('outofmemory') ||
      m.includes('makeresident') ||
      m.includes('resource-intensive') ||
      // After the WebGPU device is lost (typically VRAM exhaustion on a model
      // too big for the GPU), WebLLM disposes the pipeline; every subsequent
      // completion then throws "The current Object has already been disposed".
      // Treat it as device-loss so we surface the actionable "smaller model"
      // message AND flip to the error state (which reveals the model picker —
      // it is hidden while the card still says "loaded").
      m.includes('already been disposed') ||
      m.includes('has been disposed') ||
      m.includes('gpudevicelost')
    );
  }

  const OOM_MESSAGE =
    'GPU ran out of memory (or the graphics device was lost) running this model. ' +
    'Pick a smaller model — e.g. Phi-3.5 Mini — in the LLM panel and load it again.';

  async function ensureLoaded(modelId?: string): Promise<void> {
    const stateModelId = state.kind === 'absent' ? DEFAULT_MODEL_ID : state.modelId;
    const targetModel = modelId ?? stateModelId;
    if (state.kind === 'ready' && state.modelId === targetModel) return;
    if (inFlight) return inFlight;

    inFlight = (async () => {
      // WebGPU check first.
      const check = await checkWebGpu();
      if (!check.available) {
        setState({ kind: 'absent', reason: check.reason });
        throw new Error(check.reason);
      }
      setState({ kind: 'loading', modelId: targetModel, progress: 0, text: 'Connecting…' });
      try {
        // Dynamic import — code-split out of the pattern-only path. The Web
        // Worker engine variant moves inference off the main thread so the
        // side panel stays responsive during long-running calls.
        await createEngine(targetModel);
        setState({
          kind: 'ready',
          modelId: targetModel,
          loadedAt: Date.now(),
          engineVersion: 'web-llm-0.2',
        });
      } catch (err) {
        const message = isDeviceLost(err)
          ? OOM_MESSAGE
          : err instanceof Error
            ? err.message
            : 'engine init failed';
        setState({ kind: 'error', modelId: targetModel, message });
        throw err;
      } finally {
        inFlight = null;
      }
    })();
    return inFlight;
  }

  async function chat(req: ChatRequest): Promise<ChatResponse> {
    if (state.kind !== 'ready' || !engine) {
      throw new Error(`LLM not ready (state=${state.kind})`);
    }
    const activeModel = state.modelId;
    // For schema-typed responses, include the schema in the system prompt so
    // the model emits the expected keys. Grammar-constrained `json_object`
    // mode guarantees JSON shape but not key-set; the output-validator
    // enforces that (and extractJson recovers prose-wrapped JSON).
    const systemPrompt = req.schema
      ? `${req.systemPrompt}\n\nRespond ONLY with a single JSON object matching this schema (no prose, no markdown fences):\n${JSON.stringify(req.schema)}`
      : req.systemPrompt;
    const messages = [
      { role: 'system' as const, content: systemPrompt },
      { role: 'user' as const, content: req.userPrompt },
    ];
    const max_tokens = req.maxTokens ?? 512;

    // We deliberately do NOT use response_format:{type:'json_object'}.
    // WebLLM compiles a JSON *grammar* for that mode, and several prebuilt
    // model_lib builds hard-crash in the WASM grammar compiler
    // (`GrammarCompiler.CompileJSONSchema` → emscripten BindingError
    // "Cannot pass non-string to std::string"), surfacing as an UNCATCHABLE
    // unhandled rejection that kills the engine. The schema is instead
    // injected into the system prompt; extractJson + the output-validator
    // enforce structure.
    async function generate() {
      return engine!.chat.completions.create({
        messages,
        response_format: { type: 'text' } as const,
        max_tokens,
        temperature: 0,
      });
    }

    let reply;
    try {
      reply = await generate();
    } catch (err) {
      if (isDeviceLost(err)) {
        // GPU OOM / device lost. Recreating the same model just OOMs again
        // in a loop (the "loading again, no results" symptom). Surface an
        // actionable error and stop — the editor must pick a smaller model.
        setState({ kind: 'error', modelId: activeModel, message: OOM_MESSAGE });
        throw new Error(OOM_MESSAGE);
      }
      // The worker reports the model isn't loaded even though our state says
      // "ready" — worker eviction or a transient desync. Self-heal: rebuild
      // from cached weights (no 5 GB re-download) and retry exactly once.
      if (!isModelNotLoaded(err)) throw err;
      try {
        await engine?.unload?.();
      } catch {
        /* best-effort */
      }
      setState({ kind: 'loading', modelId: activeModel, progress: 0, text: 'Reloading model…' });
      await createEngine(activeModel);
      setState({
        kind: 'ready',
        modelId: activeModel,
        loadedAt: Date.now(),
        engineVersion: 'web-llm-0.2',
      });
      try {
        reply = await generate();
      } catch (err2) {
        if (isDeviceLost(err2)) {
          setState({ kind: 'error', modelId: activeModel, message: OOM_MESSAGE });
          throw new Error(OOM_MESSAGE);
        }
        throw err2;
      }
    }
    const text = reply.choices[0]?.message.content ?? '';
    const json = req.schema ? extractJson(text) : undefined;
    return { text, json, tokens: reply.usage?.total_tokens };
  }

  async function unload(): Promise<void> {
    if (engine?.unload) await engine.unload();
    engine = null;
    setState({ kind: 'idle', modelId: state.kind === 'ready' ? state.modelId : DEFAULT_MODEL_ID });
  }

  return {
    getState: () => state,
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    ensureLoaded,
    chat,
    unload,
  };
}

// Provider switch. `getLlmClient()` keeps its zero-arg signature (every call
// site is unchanged) and returns the client for the *active* provider; each
// provider's client is created lazily and cached. The Gemini client reads the
// BYOK key through a late-bound accessor so a key entered after creation is
// picked up live. `configureLlm` is called by the app when settings load or
// change. Default stays on-device (privacy default).
let webllmClient: LlmClient | null = null;
let geminiClient: LlmClient | null = null;
let testOverride: LlmClient | null = null;
let activeProvider: LlmProvider = 'on-device';
let geminiApiKey = '';

export function configureLlm(cfg: { provider: LlmProvider; geminiApiKey: string }): void {
  activeProvider = cfg.provider;
  geminiApiKey = cfg.geminiApiKey;
}

export function getLlmClient(): LlmClient {
  if (testOverride) return testOverride;
  if (activeProvider === 'gemini') {
    if (!geminiClient) geminiClient = createGeminiClient(() => geminiApiKey);
    return geminiClient;
  }
  if (!webllmClient) webllmClient = createClient();
  return webllmClient;
}

/** Test-only: replace the active client with a stub. */
export function _setLlmClientForTests(client: LlmClient | null): void {
  testOverride = client;
}
